export interface ActionField {
  name: string;
  label: string;
  type:
    | 'string'
    | 'number'
    | 'array'
    | 'select'
    | 'textarea'
    | 'fields-list'
    | 'potential-actions-list'
    | 'schema-field-completion'
    | 'zia-url-category'
    | 'okta-group';
  placeholder?: string;
  isRequired?: boolean;
  isConfigurable?: boolean;
  defaultValue?: any;
  options?: { title: string; value: string }[];
}

export interface ActionDefinition {
  type: string; // Internal Enum name (e.g. SLACK_SEND_WEBHOOK)
  aliases?: string[]; // Documentation names or Red Canary exec/actions strings
  label: string;
  fields: ActionField[];
}

export const SUPPORTED_ACTIONS: ActionDefinition[] = [
  {
    type: 'SEND_SMS_MESSAGE',
    aliases: ['send_sms_message', 'SendSmsMessage', 'exec/actions/send_sms_message', 'RC_SEND_SMS_MESSAGE'],
    label: 'Send SMS',
    fields: [
      {
        name: 'phone_numbers',
        label: 'Phone Numbers',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Comma-separated phone numbers',
      },
      {
        name: 'message',
        label: 'Message',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'SMS content',
      },
    ],
  },
  {
    type: 'SEND_MAIL',
    aliases: ['send_mail', 'SendMail', 'exec/actions/send_mail', 'RC_SEND_MAIL', 'SEND_EMAIL'],
    label: 'Send Email',
    fields: [
      { name: 'to', label: 'To', type: 'string', isRequired: true, isConfigurable: true, placeholder: 'recipient@example.com' },
      {
        name: 'subject',
        label: 'Subject',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Security Alert',
      },
      {
        name: 'body',
        label: 'Body',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Email body',
      },
      { name: 'cc', label: 'CC', type: 'string', placeholder: 'manager@example.com' },
      { name: 'bcc', label: 'BCC', type: 'string', placeholder: 'audit@example.com' },
      { name: 'reply_to', label: 'Reply To', type: 'string', placeholder: 'soc@example.com' },
    ],
  },
  {
    type: 'CALL_PHONE_NUMBER',
    aliases: ['call_phone_number', 'CallPhoneNumber', 'exec/actions/call_phone_number', 'RC_CALL_PHONE_NUMBER', 'RC_CALL_PHONE_NUMBERS'],
    label: 'Call Phone Number',
    fields: [
      {
        name: 'phone_numbers',
        label: 'Phone Numbers',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Comma-separated phone numbers',
      },
      {
        name: 'message',
        label: 'Message',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Voice message content',
      },
      { name: 'seconds_between_calls', label: 'Seconds Between Calls', type: 'number', placeholder: '0' },
    ],
  },
  {
    type: 'SEND_WEBHOOK',
    aliases: ['send_webhook', 'SendWebhook', 'exec/actions/send_webhook', 'RC_SEND_WEBHOOK', 'INVOKE_WEBHOOK'],
    label: 'Send Webhook',
    fields: [
      {
        name: 'url',
        label: 'URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'https://hooks.example.com/incident',
      },
      {
        name: 'httpMethod',
        label: 'HTTP Method',
        type: 'select',
        isRequired: true,
        isConfigurable: true,
        options: [
          { title: 'GET', value: 'get' },
          { title: 'POST', value: 'post' },
          { title: 'PUT', value: 'put' },
          { title: 'PATCH', value: 'patch' },
        ],
      },
      {
        name: 'payloadValue',
        label: 'Payload',
        type: 'schema-field-completion',
        placeholder: 'Webhook payload',
        isConfigurable: true,
      },
      {
        name: 'allowUntrusted',
        label: 'Allow Untrusted',
        type: 'select',
        options: [
          { title: 'True', value: 'true' },
          { title: 'False', value: 'false' },
        ],
      },
      { name: 'httpHeaders', label: 'HTTP Headers (JSON)', type: 'textarea', placeholder: '{"Header": "Value"}' },
    ],
  },
  {
    type: 'SLACK_SEND_WEBHOOK',
    aliases: ['slack_send_webhook', 'send_slack_webhook', 'SendSlackWebhook', 'exec/actions/send_slack_webhook', 'RC_SEND_SLACK_WEBHOOK'],
    label: 'Send Slack Message',
    fields: [
      {
        name: 'url',
        label: 'Webhook URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'https://hooks.slack.com/services/...',
      },
      { name: 'channel', label: 'Channel', type: 'string', placeholder: '#security-alerts', isConfigurable: true },
      {
        name: 'title',
        label: 'Message Title',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Critical alert from CrowdStrike',
      },
      { name: 'title_link', label: 'Title Link', type: 'string', placeholder: 'https://portal.example.com' },
      { name: 'text', label: 'Message Text', type: 'schema-field-completion', placeholder: 'Message text content', isConfigurable: true },
      { name: 'color', label: 'Color', type: 'string', placeholder: 'good, warning, danger, or hex code' },
    ],
  },
  {
    type: 'SEND_MS_TEAM_WEBHOOK',
    aliases: [
      'microsoft_teams_send_webhook',
      'send_ms_team_webhook',
      'SendMsTeamWebhook',
      'exec/actions/send_ms_team_webhook',
      'RC_MS_TEAM_SEND_WEBHOOK',
    ],
    label: 'Send MS Team Message',
    fields: [
      {
        name: 'url',
        label: 'Webhook URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'https://outlook.office.com/webhook/...',
      },
      {
        name: 'title',
        label: 'Message Title',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Security Alert',
      },
      {
        name: 'text',
        label: 'Message Text',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Suspicious activity detected.',
      },
    ],
  },
  {
    type: 'SEND_SYSLOG',
    aliases: ['send_syslog', 'SendSyslog', 'exec/actions/send_syslog', 'RC_SEND_SYSLOG'],
    label: 'Send Syslog',
    fields: [
      {
        name: 'serverURL',
        label: 'Server URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'syslog.example.com',
      },
      { name: 'serverPort', label: 'Server Port', type: 'number', isRequired: true, isConfigurable: true, placeholder: '514' },
      {
        name: 'protocol',
        label: 'Protocol',
        type: 'select',
        isRequired: true,
        isConfigurable: true,
        options: [
          { title: 'TCP', value: 'tcp' },
          { title: 'TCP/SSL', value: 'tcp_ssl' },
          { title: 'UDP', value: 'udp' },
        ],
      },
      { name: 'program', label: 'Program', type: 'string', isRequired: true, isConfigurable: true, placeholder: 'red_canary' },
      { name: 'message', label: 'Message', type: 'schema-field-completion', isRequired: true, isConfigurable: true },
      {
        name: 'allowUntrusted',
        label: 'Allow Untrusted',
        type: 'select',
        options: [
          { title: 'True', value: 'true' },
          { title: 'False', value: 'false' },
        ],
      },
      { name: 'facility', label: 'Facility', type: 'string' },
      { name: 'severity', label: 'Severity', type: 'string' },
    ],
  },
  {
    type: 'CROWDSTRIKE_FALCON_ISOLATE_ENDPOINT',
    aliases: [
      'crowdstrike_falcon_isolate_endpoint',
      'CrowdstrikeFalconIsolateEndpoint',
      'exec/actions/crowdstrike_falcon_isolate_endpoint',
      'RC_CS_ISOLATE_ENDPOINT',
    ],
    label: 'CrowdStrike Isolate Endpoint',
    fields: [
      { name: 'aid', label: 'Agent ID (AID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: '32-character hex ID' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'CrowdStrike credentials namespace',
      },
    ],
  },
  {
    type: 'CROWDSTRIKE_FALCON_DEISOLATE_ENDPOINT',
    aliases: [
      'crowdstrike_falcon_deisolate_endpoint',
      'CrowdstrikeFalconDeisolateEndpoint',
      'exec/actions/crowdstrike_falcon_deisolate_endpoint',
      'RC_CS_DEISOLATE_ENDPOINT',
    ],
    label: 'CrowdStrike Deisolate Endpoint',
    fields: [
      { name: 'aid', label: 'Agent ID (AID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: '32-character hex ID' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'CrowdStrike credentials namespace',
      },
    ],
  },
  {
    type: 'RC_CS_KILL_PROCESS',
    aliases: ['crowdstrike_falcon_kill_process'],
    label: 'CrowdStrike Kill Process',
    fields: [],
  },
  {
    type: 'RC_CS_DELETE_REGISTRY',
    aliases: ['crowdstrike_falcon_delete_registry'],
    label: 'CrowdStrike Delete Registry',
    fields: [],
  },
  {
    type: 'SUSPEND_OKTA_USER',
    aliases: ['okta_suspend_user', 'suspend_okta_user', 'SuspendOktaUser', 'exec/actions/suspend_okta_user', 'RC_OKTA_SUSPEND_USER'],
    label: 'Suspend Okta User',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'UNSUSPEND_OKTA_USER',
    aliases: [
      'okta_unsuspend_user',
      'unsuspend_okta_user',
      'UnsuspendOktaUser',
      'exec/actions/unsuspend_okta_user',
      'RC_OKTA_UNSUSPEND_USER',
    ],
    label: 'Unsuspend Okta User',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'CLEAR_OKTA_USER_SESSIONS',
    aliases: [
      'okta_clear_user_sessions',
      'clear_okta_user_sessions',
      'ClearOktaUserSessions',
      'exec/actions/clear_okta_user_sessions',
      'RC_OKTA_CLEAR_USER_SESSIONS',
    ],
    label: 'Clear Okta User Sessions',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'ADD_OKTA_USER_TO_GROUP',
    aliases: [
      'okta_add_user_to_group',
      'add_okta_user_to_group',
      'AddOktaUserToGroup',
      'exec/actions/add_okta_user_to_group',
      'RC_OKTA_ADD_USER_TO_GROUP',
    ],
    label: 'Add Okta User to Group',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      { name: 'okta_group_id', label: 'Okta Group', type: 'okta-group', isRequired: true, isConfigurable: true },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'REMOVE_OKTA_USER_FROM_GROUP',
    aliases: [
      'okta_remove_user_from_group',
      'remove_okta_user_from_group',
      'RemoveOktaUserFromGroup',
      'exec/actions/remove_okta_user_from_group',
      'RC_OKTA_REMOVE_USER_FROM_GROUP',
    ],
    label: 'Remove Okta User from Group',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      { name: 'okta_group_id', label: 'Okta Group', type: 'okta-group', isRequired: true, isConfigurable: true },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'ENTRA_ID_FORCE_PASSWORD_RESET',
    aliases: [
      'entra_id_force_password_reset',
      'EntraIdForcePasswordReset',
      'exec/actions/entra_id_force_password_reset',
      'RC_ENTRA_ID_FORCE_PASSWORD_RESET',
    ],
    label: 'Entra ID Force Password Reset',
    fields: [
      { name: 'user_id', label: 'User ID (UUID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: 'a1b2c3d4-...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Entra ID credentials namespace',
      },
    ],
  },
  {
    type: 'ENTRA_ID_RISKY_USER_CONFIRM_COMPROMISED',
    aliases: [
      'entra_id_risky_user_confirm_compromised',
      'EntraIdRiskyUserConfirmCompromised',
      'exec/actions/entra_id_risky_user_confirm_compromised',
      'RC_ENTRA_ID_RISKY_USER_CONFIRM_COMPROMISED',
    ],
    label: 'Entra ID Confirm Compromised',
    fields: [
      { name: 'user_id', label: 'User ID (UUID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: 'a1b2c3d4-...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Entra ID credentials namespace',
      },
    ],
  },
  {
    type: 'ENTRA_ID_CLEAR_USER_SESSIONS',
    aliases: ['entra_id_clear_user_sessions'],
    label: 'Entra ID Clear User Sessions',
    fields: [],
  },
  {
    type: 'ENTRA_ID_SUSPEND_USER',
    aliases: ['entra_id_suspend_user'],
    label: 'Entra ID Suspend User',
    fields: [],
  },
  {
    type: 'ENTRA_ID_UNSUSPEND_USER',
    aliases: ['entra_id_unsuspend_user'],
    label: 'Entra ID Unsuspend User',
    fields: [],
  },
  {
    type: 'MDE_ISOLATE_ENDPOINT_FULL',
    aliases: ['microsoft_isolate_endpoint'],
    label: 'Defender Isolate Endpoint (Full)',
    fields: [],
  },
  {
    type: 'MDE_ISOLATE_ENDPOINT_SELECTIVE',
    aliases: ['microsoft_selective_isolate_endpoint'],
    label: 'Defender Isolate Endpoint (Selective)',
    fields: [],
  },
  {
    type: 'MDE_DEISOLATE_ENDPOINT',
    aliases: ['microsoft_deisolate_endpoint'],
    label: 'Defender Release from Isolation',
    fields: [],
  },
  {
    type: 'MDE_BAN_FILE_HASHES',
    aliases: ['microsoft_ban_file_hash'],
    label: 'Defender Ban File Hash',
    fields: [],
  },
  {
    type: 'MDE_BAN_IP_ADDRESSES',
    aliases: ['microsoft_ban_ip_address'],
    label: 'Defender Ban IP Address',
    fields: [],
  },
  {
    type: 'MDE_BAN_DOMAINS',
    aliases: ['microsoft_ban_domain'],
    label: 'Defender Ban Domain',
    fields: [],
  },
  {
    type: 'MDE_QUARANTINE_FILES',
    aliases: ['microsoft_quarantine_file'],
    label: 'Defender Quarantine File',
    fields: [],
  },
  {
    type: 'MDE_RESTRICT_APP_EXECUTION',
    aliases: ['microsoft_restrict_app_execution'],
    label: 'Defender Restrict App Execution',
    fields: [],
  },
  {
    type: 'MDE_ALLOW_APP_EXECUTION',
    aliases: ['microsoft_allow_app_execution'],
    label: 'Defender Allow App Execution',
    fields: [],
  },
  {
    type: 'ADD_HASH_TO_ZSCALER_ALLOW_LIST',
    aliases: [
      'zscaler_add_hash_to_allow_list',
      'add_hash_to_zscaler_allow_list',
      'AddHashToZscalerAllowList',
      'exec/actions/add_hash_to_zscaler_allow_list',
      'RC_ZIA_ADD_HASH_TO_ALLOW_LIST',
    ],
    label: 'Zscaler Add Hash to Allow List',
    fields: [
      { name: 'hashes', label: 'Hashes', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of file hashes' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'ADD_HASH_TO_ZSCALER_BLOCK_LIST',
    aliases: [
      'zscaler_add_hash_to_block_list',
      'add_hash_to_zscaler_block_list',
      'AddHashToZscalerBlockList',
      'exec/actions/add_hash_to_zscaler_block_list',
      'RC_ZIA_ADD_HASH_TO_BLOCK_LIST',
    ],
    label: 'Zscaler Add Hash to Block List',
    fields: [
      { name: 'hashes', label: 'Hashes', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of file hashes' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'REMOVE_HASH_FROM_ZSCALER_ALLOW_LIST',
    aliases: [
      'zscaler_remove_hash_from_allow_list',
      'remove_hash_from_zscaler_allow_list',
      'RemoveHashFromZscalerAllowList',
      'exec/actions/remove_hash_from_zscaler_allow_list',
      'RC_ZIA_REMOVE_HASH_FROM_ALLOW_LIST',
    ],
    label: 'Zscaler Remove Hash from Allow List',
    fields: [
      { name: 'hashes', label: 'Hashes', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of file hashes' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'REMOVE_HASH_FROM_ZSCALER_BLOCK_LIST',
    aliases: [
      'zscaler_remove_hash_from_block_list',
      'remove_hash_from_zscaler_block_list',
      'RemoveHashFromZscalerBlockList',
      'exec/actions/remove_hash_from_zscaler_block_list',
      'RC_ZIA_REMOVE_HASH_FROM_BLOCK_LIST',
    ],
    label: 'Zscaler Remove Hash from Block List',
    fields: [
      { name: 'hashes', label: 'Hashes', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of file hashes' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'ADD_IP_TO_ZSCALER_SOURCE_IP_GROUP',
    aliases: [
      'zscaler_add_ip_to_source_ip_group',
      'add_ip_to_zscaler_source_ip_group',
      'AddIpToZscalerSourceIpGroup',
      'exec/actions/add_ip_to_zscaler_source_ip_group',
      'RC_ZIA_ADD_IP_TO_SRC_IP_GROUP',
    ],
    label: 'Zscaler Add IP to Source Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler source IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'REMOVE_IP_FROM_ZSCALER_SOURCE_IP_GROUP',
    aliases: [
      'zscaler_remove_ip_from_source_ip_group',
      'remove_ip_from_zscaler_source_ip_group',
      'RemoveIpFromZscalerSourceIpGroup',
      'exec/actions/remove_ip_from_zscaler_source_ip_group',
      'RC_ZIA_REMOVE_IP_TO_SRC_IP_GROUP',
    ],
    label: 'Zscaler Remove IP from Source Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler source IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'ADD_TO_ZSCALER_DESTINATION_IP_GROUP',
    aliases: [
      'zscaler_add_to_destination_ip_group',
      'add_to_zscaler_destination_ip_group',
      'AddToZscalerDestinationIpGroup',
      'exec/actions/add_to_zscaler_destination_ip_group',
      'RC_ZIA_ADD_IP_TO_DEST_IP_GROUP',
    ],
    label: 'Zscaler Add IP to Destination Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler destination IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'REMOVE_FROM_ZSCALER_DESTINATION_IP_GROUP',
    aliases: [
      'zscaler_remove_from_destination_ip_group',
      'remove_from_zscaler_destination_ip_group',
      'RemoveFromZscalerDestinationIpGroup',
      'exec/actions/remove_from_zscaler_destination_ip_group',
      'RC_ZIA_REMOVE_IP_FROM_DEST_IP_GROUP',
    ],
    label: 'Zscaler Remove IP from Destination Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler destination IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'ADD_TO_ZSCALER_URL_CATEGORY',
    aliases: [
      'zscaler_add_to_url_category',
      'add_to_zscaler_url_category',
      'AddToZscalerUrlCategory',
      'exec/actions/add_to_zscaler_url_category',
      'RC_ZIA_ADD_TO_URL_CATEGORY',
    ],
    label: 'Zscaler Add URL to Category',
    fields: [
      {
        name: 'category_id',
        label: 'Category',
        type: 'zia-url-category',
        isRequired: true,
        isConfigurable: true,
      },
      { name: 'urls', label: 'URLs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of URLs' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
      { name: 'configured_name', label: 'Friendly Name', type: 'string', placeholder: 'Optional friendly name' },
    ],
  },
  {
    type: 'REMOVE_FROM_ZSCALER_URL_CATEGORY',
    aliases: [
      'zscaler_remove_from_url_category',
      'remove_from_zscaler_url_category',
      'RemoveFromZscalerUrlCategory',
      'exec/actions/remove_from_zscaler_url_category',
      'RC_ZIA_REMOVE_FROM_URL_CATEGOPRY',
    ],
    label: 'Zscaler Remove URL from Category',
    fields: [
      {
        name: 'category_id',
        label: 'Category',
        type: 'zia-url-category',
        isRequired: true,
        isConfigurable: true,
      },
      { name: 'urls', label: 'URLs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of URLs' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
      { name: 'configured_name', label: 'Friendly Name', type: 'string', placeholder: 'Optional friendly name' },
    ],
  },
];
